import { Smartphone } from '@mui/icons-material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import HardwareIcon from '@mui/icons-material/Hardware';
import HttpsIcon from '@mui/icons-material/Https';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Checkbox,
    FormControlLabel,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { DesktopDatePicker, TimePicker } from '@mui/x-date-pickers';
import { Q } from '@nozbe/watermelondb';
import { useFormik } from 'formik';
import { isEqual } from 'lodash';
import moment, { Moment as MomentInterface } from 'moment';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { firstValueFrom } from 'rxjs';
import { SchedulesAnalytics } from 'shared/analytics/schedules';
import { CountryCode } from 'shared/constants/countries';
import { CONTACT_REMINDER, USER_REMINDER } from 'shared/constants/event';
import EventContactReminder from 'shared/db/models/EventContactReminder';
import Event from 'shared/db/services/Event';
import EventContact from 'shared/db/services/EventContact';
import EventUser from 'shared/db/services/EventUser';
import OrganisationUser from 'shared/db/services/OrganisationUser';
import Region from 'shared/db/services/Region';
import User from 'shared/db/services/User';
import { ContactModel } from 'shared/types/Contacts';
import {
    EventPayload,
    EventCategory,
    ContactInvitations,
    ContactInvitation,
    ContactRSVPStatus as ContactRSVPStatusType,
} from 'shared/types/Events';
import { HorseModel } from 'shared/types/Horses';
import { Location } from 'shared/types/Location';
import { EventTimezone } from 'shared/types/timezone';
import { UserModel, UserPayload } from 'shared/types/User';
import { calculateContactInvitationState } from 'shared/utils/events';
import Moment from 'shared/utils/moment';
import { getTimeZonesForRegions } from 'shared/utils/timezone';

import { CalendarEventIcon, ScheduleIcon } from '@/assets/svg';
import {
    AddEditContactModal,
    AddEditHorseModal,
    AddEditModalHeader,
    AppointmentInviteContactSection,
    GooglePlacesAutocomplete,
    MembersSelection,
    Modal,
    RemoveEntityAlert,
    SelectContactsSection,
    SelectHorsesSection,
    Tabs,
    Tooltip,
    NoAppointmentContactDetailsModal,
    AppointmentInvitationInPastModal,
    ContactRSVPStatus,
    BlockedPremiumFeatureModal,
} from '@/components';
import { TabItem, TabLayout } from '@/components/Tabs/types';
import { APPLE_APP_STORE_URL, GOOGLE_PLAY_URL } from '@/constants/mobileStores';
import { useDBSyncContext } from '@/context/DBSyncContext';
import { useImagesContext } from '@/context/ImagesContext';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useUserContext } from '@/context/UserContext';
import {
    useBrowserExitPrompt,
    useCancelEntityFormAlert,
    useDatabase,
    useEscapeButton,
    useIsPremiumFeatureAvailable,
} from '@/hooks';
import { useEntityImage } from '@/hooks/useEntityImage';
import { databaseValidation } from '@/services/databaseValidation';
import { FirebaseAnalytics } from '@/services/firebase/analytics';
import { FirebaseFirestoreAPI } from '@/services/firebase/firestore';
import Logger from '@/services/logger';
import { dbServiceLogger } from '@/services/logger/logger';
import { Snackbar } from '@/services/toastNotifications';
import { mapUsersWithColors } from '@/services/user';
import { UserWithColor } from '@/services/user/types';
import regionStore from '@/stores/userRegion';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';
import { getImageDisplayUrl } from '@/utils/images';
import { ScheduleValidationSchema } from '@/validations/schedule';

import Spotlight from '../Spotlight';
import TooltipSyncButton from '../TooltipButton';

import { AppointmentOnboardingModal } from './components';
import SelectWithCustomInput from './components/SelectWithCustomInput';
import {
    createUserReminderOptions,
    getOptionFromReminderValue,
    createContactsReminderOptions,
} from './components/utils';
import { CUSTOM_REMINDER_OPTIONS } from './constants';
import { Props, TitleState, AddEditScheduleFormValues } from './types';

function AddEditScheduleModal({
    contacts,
    mode = 'create',
    onClose,
    event,
    slotInfo,
    view,
    horses,
    onRemove,
    onSubmit,
    onUpdate,
    closeOnSubmit = false,
}: Props) {
    const { t } = useTranslation();
    const { getDatabase } = useDatabase();
    const { userProfileData } = useUserContext();
    const { ImagesService } = useImagesContext();
    const { isSyncInProgress } = useDBSyncContext();
    const { userOrganisation, userOrganisations } = useOrganisationsContext();
    const { isPremiumFeatureAvailable } = useIsPremiumFeatureAvailable();

    const contactsReminderAvailable = isPremiumFeatureAvailable;
    const shouldLockPremiumFeature = !isPremiumFeatureAvailable;

    const [contactsInvitationState, setContactsInvitationState] =
        useState<ContactInvitations>({});
    const [contactsRSVPStatus, setContactsRSVPStatus] =
        useState<ContactRSVPStatusType>({});
    const [tabsLayout, setTabsLayout] = useState<TabLayout>({
        width: 0,
        height: 0,
    });
    const [showNoPhoneModal, setShowNoPhoneModal] = useState(false);
    const [showNoEmailModal, setShowNoEmailModal] = useState(false);
    const [
        showBlockedPremiumFeaturesModal,
        setShowBlockedPremiumFeaturesModal,
    ] = useState(false);
    const [contactToEdit, setContactToEdit] = useState<ContactModel | null>(
        null,
    );
    const [showEditContactModal, setShowEditContactModal] = useState(false);
    const [showInPastModal, setShowInPastModal] = useState(false);
    const [initialNumberOfInvitedContacts, setInitialNumberOfInvitedContacts] =
        useState(0);
    const [isInPast, setIsInPast] = useState(false);
    const scrollContainerRef = useRef<HTMLElement>();

    const getNumberOfInvitedContacts = useCallback(
        (contactsState: ContactInvitations) =>
            Object.values(contactsState).filter(
                (state) => state.email || state.sms,
                [],
            ).length,
        [],
    );

    const numberOfInvitedContacts = useMemo(
        () => getNumberOfInvitedContacts(contactsInvitationState),
        [contactsInvitationState, getNumberOfInvitedContacts],
    );

    const defaultStandardTitle = t('Event:default_titles:standard_appointment');
    const defaultServiceTitle = t('Event:default_titles:service_appointment');

    const teamAvatar = useEntityImage(userOrganisation?.id || '');

    const tabItems: TabItem[] = useMemo(
        () => [
            {
                label: t('Event:categories:service'),
                value: EventCategory.service,
                icon: (
                    <HardwareIcon
                        className="mr-0"
                        width={21}
                        height={23}
                        htmlColor={ENTITY_COLOR.schedule}
                    />
                ),
            },
            {
                label: t('Event:categories:standard'),
                value: EventCategory.standard,
                icon: (
                    <CalendarEventIcon
                        width={24}
                        height={26}
                        color={ENTITY_COLOR.schedule}
                    />
                ),
            },
        ],
        [t],
    );

    // INFO: this hook open browser prompt when we refresh or leave the browser
    // so the user dont lose form information without confirmation
    useBrowserExitPrompt();

    const database = useMemo(() => getDatabase(), [getDatabase]);

    const userService = useMemo(
        () =>
            new User({
                database,
                imageService: ImagesService,
                logDBAction: Logger.logRecordActivity,
                validation: databaseValidation,
            }),
        [ImagesService, database],
    );

    const eventUserService = useMemo(
        () =>
            new EventUser({
                database,
                imageService: ImagesService,
                logDBAction: Logger.logRecordActivity,
                validation: databaseValidation,
            }),
        [ImagesService, database],
    );

    const regionService = useMemo(() => new Region(database), [database]);

    const getInitialValues: (
        horses?: HorseModel[],
    ) => AddEditScheduleFormValues = useCallback(
        (horses?: HorseModel[]) => ({
            contacts: [],
            horses: horses || [],
            address: '',
            contactsReminder: false,
            contactsReminderTime: CONTACT_REMINDER.BEFORE1DAY,
            startTime: Moment(),
            date: Moment(),
            endTime: Moment().add(1, 'h'),
            membersIds: [],
            notes: '',
            userReminder: USER_REMINDER.NONE,
            timezone: '',
            createEntries: false,
            contactsInvitationState: {},
        }),
        [],
    );

    const defaultInitialValues = useMemo(
        () => getInitialValues(horses),
        [getInitialValues, horses],
    );

    const [initialValues, setInitialValues] =
        useState<AddEditScheduleFormValues>(defaultInitialValues);

    const [isRemoveEventAlertVisible, setIsRemoveEventAlertVisible] =
        useState<boolean>(false);

    const [organisationUsers, setOrganisationUsers] = useState<
        UserWithColor[] | null
    >(null);

    const [eventUsersIds, setEventUsersIds] = useState<string[] | null>(null);

    const [selectableTimezones, setSelectableTimezones] = useState<
        EventTimezone[]
    >([]);

    const [appointmentCategory, setAppointmentCategory] =
        useState<EventCategory>(event?.category || EventCategory.service);

    const isServiceType = appointmentCategory === EventCategory.service;

    const initialTitleValue = useMemo(() => {
        if ((mode === 'duplicate' || mode === 'edit') && event) {
            return event?.title ?? '';
        }

        return isServiceType ? defaultServiceTitle : defaultStandardTitle;
    }, [event, mode, defaultServiceTitle, defaultStandardTitle, isServiceType]);

    const [title, setTitle] = useState<TitleState>({
        dirty: false,
        touched: false,
        error: null,
        value: initialTitleValue,
    });

    const updateTitleState = useCallback(
        (newState: Partial<TitleState>) => {
            setTitle((prev) => ({
                ...prev,
                ...newState,
                dirty: newState.value !== initialTitleValue,
            }));
        },
        [initialTitleValue],
    );

    useEffect(() => {
        setTitle((prev) => ({
            ...prev,
            dirty: prev.value !== initialTitleValue,
        }));
    }, [initialTitleValue]);

    const validateTitle = useCallback(async (value: string) => {
        try {
            await ScheduleValidationSchema.titleField.validate(value);
        } catch (e) {
            if (e.name === 'ValidationError') {
                return e.message;
            }
        }

        return null;
    }, []);

    const [userModel, setUserModel] = useState<UserModel | null>(null);

    const fetchUserModel = useCallback(async () => {
        const [model] = await userService
            .getById(userProfileData?.id ?? '')
            .fetch();
        if (model) {
            setUserModel(model);
        }
    }, [userProfileData?.id, userService]);

    useEffect(() => {
        fetchUserModel();
    }, [fetchUserModel]);

    const dismissAppointmentOnboarding = useCallback(async () => {
        const model = await userService.update(userProfileData?.id ?? '', {
            isAppointmentOnboarded: true,
        });

        setUserModel(model);
    }, [userProfileData?.id, userService]);

    const fetchOrganisationUsers = useCallback(async () => {
        const organisationUserService = new OrganisationUser(database);

        const users = await userService.get().fetch();

        const activeUsers: UserModel[] = [];

        for (const user of users) {
            const [organisationUser] = await organisationUserService
                .getById(user.id)
                .fetch();

            if (organisationUser?.active) {
                activeUsers.push(user);
            }
        }

        const usersWithColors = await mapUsersWithColors(activeUsers, database);

        setOrganisationUsers(usersWithColors);
    }, [database, userService]);

    const fetchEventUsersIds = useCallback(async () => {
        if (event?.id) {
            const eventUsers = await firstValueFrom(
                eventUserService.getEventUsersIdsById(event.id),
            );

            setEventUsersIds(eventUsers);
        }
    }, [event?.id, eventUserService]);

    useEffect(() => {
        fetchOrganisationUsers();

        if (mode === 'duplicate' || mode === 'edit') {
            fetchEventUsersIds();
        }
    }, [fetchEventUsersIds, fetchOrganisationUsers, mode]);

    const getAcceptedTimezone = useCallback(
        (timezones: EventTimezone[], timezoneToSearch = '') =>
            timezones.find((timezone) => timezone.timezone === timezoneToSearch)
                ?.timezone || '',
        [],
    );

    const getTimezones = useCallback(
        async (date?: MomentInterface) => {
            const regions = await regionService.getAll().fetch();

            return getTimeZonesForRegions(regions, date);
        },
        [regionService],
    );

    const prepareInitialValues = useCallback(async () => {
        const deviceTimezone = Moment.tz.guess();

        const userRegionDefaultTimezone = (
            await regionService
                .getByParam('code', userProfileData?.region ?? '')
                .fetch()
        )[0]?.defaultTimezone;

        const userTimezone = userModel?.timezone;

        const timezones = await getTimezones(
            event ? Moment(event.startsTime) : undefined,
        );

        setSelectableTimezones(timezones);

        const initialTimezone =
            getAcceptedTimezone(timezones, userTimezone) ||
            getAcceptedTimezone(timezones, deviceTimezone) ||
            getAcceptedTimezone(timezones, userRegionDefaultTimezone) ||
            timezones[0]?.timezone ||
            '';

        if ((mode === 'duplicate' || mode === 'edit') && event) {
            try {
                const eventContactService = new EventContact({
                    database,
                    imageService: ImagesService,
                    logDBAction: Logger.logRecordActivity,
                    validation: databaseValidation,
                });
                const eventContactReminderService =
                    database.collections.get<EventContactReminder>(
                        'event_contact_reminders',
                    );
                const eventContactReminders = event.id
                    ? await eventContactReminderService
                          .query(Q.where('event_id', Q.eq(event.id)))
                          .fetch()
                    : [];

                const eventContacts = await event.contacts?.fetch();
                const eventHorses = await event.horses?.fetch();
                const eventLocation = await event.location?.fetch();
                const eventContactsReminders =
                    eventContactReminders[0]?.timeBefore;
                const eventContactsInvitations = await eventContactService
                    .getByEventId(event.id ?? '')
                    .fetch();

                const invitationState: ContactInvitations = eventContacts
                    ? eventContactsInvitations.reduce(
                          (previous, current) => ({
                              ...previous,
                              [current.contactId]: {
                                  sms: current.sentSms,
                                  email: current.sentEmail,
                                  smsAt: current.sentSmsAt || null,
                                  emailAt: current.sentEmailAt || null,
                                  smsStatus:
                                      mode === 'duplicate'
                                          ? 'not_sent'
                                          : calculateContactInvitationState(
                                                current.sentSms,
                                                current.sentSmsAt,
                                            ),
                                  emailStatus:
                                      mode === 'duplicate'
                                          ? 'not_sent'
                                          : calculateContactInvitationState(
                                                current.sentEmail,
                                                current.sentEmailAt,
                                            ),
                              },
                          }),
                          {},
                      )
                    : {};

                setInitialNumberOfInvitedContacts(
                    getNumberOfInvitedContacts(invitationState),
                );

                setContactsInvitationState(invitationState);

                setContactsRSVPStatus(
                    eventContactsInvitations?.reduce(
                        (previous, current) => ({
                            ...previous,
                            [current.contactId]: current.status,
                        }),
                        {},
                    ) ?? {},
                );

                const selectedTimezone =
                    getAcceptedTimezone(timezones, event?.timezone) ||
                    initialTimezone;

                const values: AddEditScheduleFormValues = {
                    endTime: Moment(event.endsTime).tz(selectedTimezone, false),
                    startTime: Moment(event.startsTime).tz(
                        selectedTimezone,
                        false,
                    ),
                    date: Moment(event.startsTime).tz(selectedTimezone, false),
                    address: event.address ?? '',
                    notes: event.notes ?? '',
                    userReminder: event.userReminder ?? USER_REMINDER.NONE,
                    contactsReminder: event.contactsReminder ?? false,
                    contactsReminderTime:
                        eventContactsReminders ??
                        event.userReminder ??
                        CONTACT_REMINDER.BEFORE1DAY,
                    horses: eventHorses ?? [],
                    contacts: eventContacts ?? [],
                    membersIds: eventUsersIds ?? [],
                    location: eventLocation || null,
                    timezone: selectedTimezone,
                    createEntries:
                        mode === 'duplicate'
                            ? !!userModel?.isEntryCreationFromEventEnabled
                            : undefined,
                };

                updateTitleState({
                    value: event.title ?? '',
                });

                setInitialValues(values);
            } catch {
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: {
                        variant: 'error',
                    },
                });
            }
        } else if (mode === 'create') {
            let startTime = Moment().tz(initialTimezone, false);
            let endTime = Moment().add(1, 'h').tz(initialTimezone, false);

            let date = startTime;

            if (slotInfo) {
                const keepLocalTime = view === 'month';
                const start = Moment(slotInfo.start).tz(
                    initialTimezone,
                    keepLocalTime,
                );
                const end = Moment(slotInfo.end).tz(
                    initialTimezone,
                    keepLocalTime,
                );

                date = start;

                if (view !== 'month') {
                    startTime = start;
                    endTime = end;
                }
            }

            setInitialValues((values) => ({
                ...values,
                date,
                startTime,
                endTime,
                contacts: contacts || [],
                membersIds:
                    organisationUsers
                        ?.map((member) => member.user.id)
                        .filter((id) => id === userProfileData?.id) || [],
                timezone: initialTimezone,
                createEntries: !!userModel?.isEntryCreationFromEventEnabled,
            }));
        }
    }, [
        slotInfo,
        view,
        regionService,
        userProfileData?.region,
        userProfileData?.id,
        userModel?.timezone,
        userModel?.isEntryCreationFromEventEnabled,
        getTimezones,
        event,
        getAcceptedTimezone,
        mode,
        database,
        ImagesService,
        getNumberOfInvitedContacts,
        eventUsersIds,
        updateTitleState,
        t,
        contacts,
        organisationUsers,
    ]);

    useEffect(() => {
        prepareInitialValues();
    }, [prepareInitialValues]);

    const shouldNotifyContacts = useCallback(
        (values: AddEditScheduleFormValues) => {
            // We don't want to move on deeper if this scenario passes
            // Because we will only show the modal if the checkbox was and continue to be ticked
            if (!numberOfInvitedContacts) {
                return false;
            }

            if (
                !values.date.isSame(initialValues.date) ||
                !values.startTime.isSame(initialValues.startTime) ||
                !values.endTime.isSame(initialValues.endTime) ||
                values.timezone !== initialValues.timezone ||
                !isEqual(values.horses, initialValues.horses) ||
                values.address !== initialValues.address ||
                values.notes !== initialValues.notes
            ) {
                return true;
            }
        },
        [
            initialValues.address,
            initialValues.date,
            initialValues.endTime,
            initialValues.horses,
            initialValues.notes,
            initialValues.startTime,
            initialValues.timezone,
            numberOfInvitedContacts,
        ],
    );

    const getAlertMinTime = useCallback(
        (reminder: string | null | undefined) => {
            if (!reminder) {
                return null;
            }

            const now = Moment();

            const timeRange = reminder.split('')[reminder.length - 1];
            const timeNumber = Number(reminder.split(timeRange)[0]);

            switch (timeRange) {
                case 'm':
                    return now.add(timeNumber, 'minutes');
                case 'h':
                    return now.add(timeNumber, 'hours');
                case 'd':
                    return now.add(timeNumber, 'days');
                case 'w':
                    return now.add(timeNumber, 'weeks');
                default:
                    return null;
            }
        },
        [],
    );

    const handleSubmit = useCallback(
        async (values: AddEditScheduleFormValues) => {
            try {
                const titleError = await validateTitle(title.value);
                const notifyContacts = shouldNotifyContacts(values);

                if (titleError) {
                    updateTitleState({
                        error: titleError,
                    });

                    return;
                }

                const eventService = new Event({
                    database,
                    imageService: ImagesService,
                    notificationsService: {
                        cancelNotification: () => null,
                        scheduleEventLocalNotification: () => Promise.resolve(),
                    },
                    cacheService: {
                        clear: () => Promise.resolve(),
                        get: (() => Promise.resolve()) as () => any,
                    },
                    firestoreService: {
                        addDocumentToCollection:
                            FirebaseFirestoreAPI.addDocumentToCollection,
                    },
                    logger: dbServiceLogger,
                    validation: databaseValidation,
                });

                const {
                    endTime,
                    startTime,
                    date,
                    contacts,
                    contactsReminder,
                    contactsReminderTime,
                    userReminder,
                    horses,
                    membersIds,
                    address,
                    notes,
                    location,
                    createEntries,
                    timezone,
                } = values;

                const contactsReminderTimeBefore =
                    contactsReminderAvailable && contactsReminder
                        ? contactsReminderTime
                        : undefined;

                const payload: EventPayload = {
                    startsTime: date
                        .hour(startTime.hour())
                        .minute(startTime.minute())
                        .tz(timezone, true)
                        .toISOString(),
                    endsTime: date
                        .hour(endTime.hour())
                        .minute(endTime.minute())
                        .tz(timezone, true)
                        .toISOString(),
                    title: title.value,
                    address,
                    notes,
                    userReminder:
                        userReminder && userReminder !== USER_REMINDER.NONE
                            ? userReminder
                            : null,
                    contactsReminder,
                    contactsReminderTimeBefore,
                    horses: isServiceType ? horses : [],
                    contacts,
                    ownersReminder: false, // TODO: change this when checkbox will be enabled on UI
                    location,
                    membersIds,
                    createEntries:
                        isServiceType && horses?.length ? createEntries : false,
                    timezone,
                    category: appointmentCategory,
                    invitations: contactsInvitationState,
                };

                if (mode === 'edit') {
                    if (!event?.id) {
                        throw new Error();
                    }

                    const updatedEvent = await eventService.update({
                        id: event.id,
                        notifyContacts,
                        payload,
                        userId: userProfileData?.id || '',
                    });

                    SchedulesAnalytics.logUserUpdatedEvent(
                        FirebaseAnalytics.logEvent,
                        {
                            event: updatedEvent,
                            previousContactsReminder: event.contactsReminder,
                            previousUserReminder: event.userReminder,
                            numberOfInvitedContacts,
                            previousNumberOfInvitedContacts:
                                initialNumberOfInvitedContacts,
                            membersIds: payload.membersIds ?? [],
                        },
                    );

                    onUpdate && onUpdate(updatedEvent);
                } else {
                    const createdEvent = await eventService.add({
                        payload,
                        userId: userProfileData?.id || '',
                    });

                    SchedulesAnalytics.logUserCreatedEvent(
                        FirebaseAnalytics.logEvent,
                        {
                            event: createdEvent,
                            isOwner: userOrganisation?.owner ?? true,
                            numberOfInvitedContacts,
                            membersIds: payload.membersIds ?? [],
                        },
                    );

                    onSubmit(createdEvent);
                }

                const userPayload: UserPayload = { timezone };

                if (mode !== 'edit') {
                    userPayload.isEntryCreationFromEventEnabled = createEntries;
                }

                await userService.update(
                    userProfileData?.id ?? '',
                    userPayload,
                );

                Snackbar.showToastNotification({
                    message: t(
                        mode === 'edit'
                            ? 'App:Messages:has_been_edited_successfully'
                            : 'App:Messages:has_been_created_successfully',
                        {
                            entity: t('Entities:event'),
                        },
                    ),
                });

                if (mode === 'edit' || closeOnSubmit) {
                    onClose();
                }
            } catch {
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: {
                        variant: 'error',
                    },
                });
            }
        },
        [
            validateTitle,
            title.value,
            shouldNotifyContacts,
            database,
            ImagesService,
            contactsReminderAvailable,
            isServiceType,
            appointmentCategory,
            contactsInvitationState,
            mode,
            userService,
            userProfileData?.id,
            t,
            closeOnSubmit,
            updateTitleState,
            event?.id,
            event?.contactsReminder,
            event?.userReminder,
            numberOfInvitedContacts,
            initialNumberOfInvitedContacts,
            onUpdate,
            userOrganisation?.owner,
            onSubmit,
            onClose,
        ],
    );

    const form = useFormik<AddEditScheduleFormValues>({
        enableReinitialize: true,
        initialValues,
        onSubmit: handleSubmit,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: false,
        validationSchema: ScheduleValidationSchema.addEditScheduleForm,
    });

    const getInvitationStateBasedOnIsInPastState = useCallback(
        (
            isInPastState: boolean,
            currentState: ContactInvitation,
            contact: ContactModel,
        ): ContactInvitation => ({
            ...currentState,
            email:
                currentState.emailStatus === 'not_sent' &&
                contact.phone &&
                isPremiumFeatureAvailable
                    ? !isInPastState
                    : currentState.email,
            sms:
                currentState.smsStatus === 'not_sent' &&
                contact.email &&
                isPremiumFeatureAvailable
                    ? !isInPastState
                    : currentState.sms,
        }),
        [isPremiumFeatureAvailable],
    );

    const updateInPastState = useCallback(() => {
        const { date, timezone, startTime, contacts } = form.values;

        if (date && timezone && startTime && contacts) {
            setIsInPast((isInPastState) => {
                const currentIsInPastState = date
                    .hour(startTime.hour())
                    .minute(startTime.minute())
                    .tz(timezone, true)
                    .isBefore(moment().tz(timezone, false));

                if (isInPastState === currentIsInPastState) {
                    return isInPastState;
                }

                setContactsInvitationState((state) =>
                    contacts.reduce(
                        (previous, current) => ({
                            ...previous,
                            [current.id]:
                                getInvitationStateBasedOnIsInPastState(
                                    currentIsInPastState,
                                    state[current.id] ?? {
                                        sms: false,
                                        email: false,
                                        smsAt: '',
                                        emailAt: '',
                                        smsStatus: 'not_sent',
                                        emailStatus: 'not_sent',
                                    },
                                    current,
                                ),
                        }),
                        {},
                    ),
                );

                return currentIsInPastState;
            });
        }
    }, [form.values, getInvitationStateBasedOnIsInPastState]);

    useEffect(() => {
        updateInPastState();
    }, [updateInPastState]);

    const onStartTimeChanged = useCallback(
        (value) => form.setFieldValue('startTime', value),
        [form],
    );

    const onEndTimeChanged = useCallback(
        (value) => form.setFieldValue('endTime', value),
        [form],
    );

    const onDateChanged = useCallback(
        async (value) => {
            form.setFieldValue('date', value);
            const timezones = await getTimezones(value);
            setSelectableTimezones(timezones);
        },
        [form, getTimezones],
    );

    const renderDateTimePickerInput = useCallback(
        (params, field: string, testID: string) => (
            <TextField
                {...params}
                fullWidth
                error={!!form.errors[field] && !!form.touched[field]}
                helperText={form.touched[field] ? form.errors[field] : ''}
                onBlur={form.handleBlur}
                name={field}
                id={field}
                data-test-id={testID}
                aria-readonly={false}
            />
        ),
        [form.errors, form.handleBlur, form.touched],
    );

    const renderDateInput = useCallback(
        (params) =>
            renderDateTimePickerInput(
                params,
                'date',
                'AddEditScheduleForm-Date',
            ),
        [renderDateTimePickerInput],
    );
    const renderStartTimeInput = useCallback(
        (params) =>
            renderDateTimePickerInput(
                params,
                'startTime',
                'AddEditScheduleForm-StartTime',
            ),
        [renderDateTimePickerInput],
    );
    const renderEndTimeInput = useCallback(
        (params) =>
            renderDateTimePickerInput(
                params,
                'endTime',
                'AddEditScheduleForm-EndTime',
            ),
        [renderDateTimePickerInput],
    );

    const handleSingleContactLocationPrefill = useCallback(
        async (contact: ContactModel) => {
            if (!contact.address) return;

            try {
                form.setFieldValue('address', contact.address);

                const contactLocation = await contact.location?.fetch();

                if (contactLocation) {
                    form.setFieldValue('location', contactLocation);
                }
            } catch (error) {
                Logger.logError(
                    `(handleSingleContactLocationPrefill) error: ${error}`,
                );
            }
        },
        [form],
    );

    const handleSelectContacts = useCallback(
        async (contacts: ContactModel[]) => {
            setContactsInvitationState((state) =>
                contacts.reduce(
                    (previous, current) => ({
                        ...previous,
                        [current.id]: {
                            sms:
                                state[current.id]?.sms ??
                                (!isInPast &&
                                    !!current.phone &&
                                    !shouldLockPremiumFeature),
                            email:
                                state[current.id]?.email ??
                                (!isInPast &&
                                    !!current.email &&
                                    !shouldLockPremiumFeature),
                            smsAt: state[current.id]?.smsAt ?? null,
                            emailAt: state[current.id]?.emailAt ?? null,
                            smsStatus:
                                state[current.id]?.smsStatus ?? 'not_sent',
                            emailStatus:
                                state[current.id]?.emailStatus ?? 'not_sent',
                        },
                    }),
                    {},
                ),
            );
            form.setFieldValue('contacts', contacts);

            if (contacts.length === 1) {
                const onlyContact = contacts[0];

                if (!onlyContact.address) return;

                await handleSingleContactLocationPrefill(onlyContact);
            }
        },
        [
            form,
            isInPast,
            shouldLockPremiumFeature,
            handleSingleContactLocationPrefill,
        ],
    );

    const handleRemoveContact = useCallback(
        (contact: ContactModel) => {
            const previousContacts = form.values.contacts;
            const newContacts = previousContacts?.filter(
                (prevContact) => prevContact.id !== contact.id,
            );

            form.setFieldValue('contacts', newContacts);
        },
        [form],
    );

    const handleSelectHorses = useCallback(
        (horses: HorseModel[]) => form.setFieldValue('horses', horses),
        [form],
    );

    const handleRemoveHorse = useCallback(
        (horse: HorseModel) => {
            const previousHorses = form.values.horses;
            const newHorses = previousHorses?.filter(
                (prevHorse) => prevHorse.id !== horse.id,
            );

            form.setFieldValue('horses', newHorses);
        },
        [form],
    );

    const willSendUserAlert = useMemo(() => {
        const { date, startTime, userReminder } = form.values;

        if (
            !date?.isValid() ||
            !startTime?.isValid() ||
            userReminder === undefined
        ) {
            return true;
        }

        const startDate = date
            .hour(startTime.hour())
            .minute(startTime.minute());

        const alertMinTime = getAlertMinTime(userReminder)?.tz(
            form.values.timezone,
            true,
        );

        return (
            !alertMinTime ||
            startDate.tz(form.values.timezone, true).isAfter(alertMinTime)
        );
    }, [form.values, getAlertMinTime]);

    const willSendContactsAlert = useMemo(() => {
        const { date, startTime, contactsReminderTime } = form.values;

        if (
            !date?.isValid() ||
            !startTime?.isValid() ||
            contactsReminderTime === undefined
        ) {
            return true;
        }

        const startDate = date
            .hour(startTime.hour())
            .minute(startTime.minute());

        const alertMinTime = getAlertMinTime(contactsReminderTime)?.tz(
            form.values.timezone,
            true,
        );

        return (
            !alertMinTime ||
            startDate.tz(form.values.timezone, true).isAfter(alertMinTime)
        );
    }, [form.values, getAlertMinTime]);

    const showRemoveEventAlert = useCallback(() => {
        setIsRemoveEventAlertVisible(true);
    }, []);

    const closeRemoveEventAlert = useCallback(
        () => setIsRemoveEventAlertVisible(false),
        [],
    );

    const handleRemoveEvent = useCallback(async () => {
        try {
            const eventService = new Event({
                database,
                imageService: ImagesService,
                notificationsService: {
                    cancelNotification: () => null,
                    scheduleEventLocalNotification: () => Promise.resolve(),
                },
                cacheService: {
                    clear: () => Promise.resolve(),
                    get: (() => Promise.resolve()) as () => any,
                },
                logger: dbServiceLogger,
                validation: databaseValidation,
            });

            await eventService.deleteByID(
                event?.id ?? '',
                userProfileData?.id ?? '',
            );

            SchedulesAnalytics.logUserDeletedEvent(FirebaseAnalytics.logEvent);

            Snackbar.showToastNotification({
                message: t('App:Messages:has_been_removed_successfully', {
                    entity: t('Entities:event'),
                }),
            });

            onClose();

            if (onRemove) {
                onRemove();
            }
        } catch {
            Snackbar.showToastNotification({
                message: t('App:Messages:something_went_wrong'),
                options: {
                    variant: 'error',
                },
            });
        }
    }, [
        ImagesService,
        onClose,
        database,
        event?.id,
        t,
        userProfileData?.id,
        onRemove,
    ]);

    const handlePlaceChange = useCallback(
        (location: Location) => {
            form.setFieldValue('location', location);
            form.setFieldValue('address', location?.name || '');
        },
        [form],
    );

    const { handleFormCancelButtonClick, renderCancelAlert, showCancelAlert } =
        useCancelEntityFormAlert({
            closeEntityForm: onClose,
            shouldShowAlertOnCancel: form.dirty || title.dirty,
        });

    useEscapeButton(handleFormCancelButtonClick);

    const renderSelectedTimezone = useCallback(
        (timezoneValue) => {
            const selectedTimezone = selectableTimezones.find(
                ({ timezone }) => timezone === timezoneValue,
            );
            return `(${selectedTimezone?.offset}) ${selectedTimezone?.displayName}`;
        },
        [selectableTimezones],
    );

    const setDefaultTitle = useCallback(
        (type: EventCategory) => {
            if (mode === 'edit' || mode === 'duplicate') {
                return;
            }

            if (type === EventCategory.standard) {
                if (!title.dirty) {
                    updateTitleState({
                        value: defaultStandardTitle,
                    });
                }
            }

            if (type === EventCategory.service) {
                if (!title.dirty) {
                    updateTitleState({
                        value: defaultServiceTitle,
                    });
                }
            }
        },
        [
            mode,
            title.dirty,
            defaultServiceTitle,
            defaultStandardTitle,
            updateTitleState,
        ],
    );

    const handleTabChange = useCallback(
        (category: EventCategory) => {
            setAppointmentCategory(category);
            setDefaultTitle(category);
        },
        [setDefaultTitle],
    );

    const handleTabsLayout = useCallback(
        (layout: TabLayout) => setTabsLayout(layout),
        [],
    );

    const handleTitleBlur = useCallback(() => {
        setTitle((prev) => ({ ...prev, touched: true }));
    }, []);

    const handleTitleChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            const error = await validateTitle(newValue);

            updateTitleState({
                error,
                value: newValue,
                touched: true,
            });
        },
        [validateTitle, updateTitleState],
    );

    const handleContactSmsClick = useCallback(
        (contact: ContactModel) =>
            setContactsInvitationState((state) => ({
                ...state,
                [contact.id]: {
                    ...state[contact.id],
                    sms:
                        state[contact.id]?.sms != null
                            ? !state[contact.id].sms
                            : false,
                },
            })),
        [],
    );

    const handleContactEmailClick = useCallback(
        (contact: ContactModel) =>
            setContactsInvitationState((state) => ({
                ...state,
                [contact.id]: {
                    ...state[contact.id],
                    email:
                        state[contact.id]?.email != null
                            ? !state[contact.id].email
                            : false,
                },
            })),
        [],
    );

    const handleContactSmsDisabledClick = useCallback(
        (contact: ContactModel) => {
            if (isInPast) {
                setShowInPastModal(true);
            } else {
                setShowNoPhoneModal(true);
                setContactToEdit(contact);
            }
        },
        [isInPast],
    );

    const handleContactEmailDisabledClick = useCallback(
        (contact: ContactModel) => {
            if (isInPast) {
                setShowInPastModal(true);
            } else {
                setShowNoEmailModal(true);
                setContactToEdit(contact);
            }
        },
        [isInPast],
    );

    const handleEditContact = useCallback(() => {
        setShowEditContactModal(true);
    }, []);

    const handleCloseNoDetailsModal = useCallback(() => {
        setShowNoEmailModal(false);
        setShowNoPhoneModal(false);
        setContactToEdit(null);
    }, []);

    const closeEditContactModal = useCallback(() => {
        setShowEditContactModal(false);
        setContactToEdit(null);
        handleCloseNoDetailsModal();
    }, [handleCloseNoDetailsModal]);

    const handleSaveContact = useCallback(
        (contact: ContactModel) => {
            if (
                showNoPhoneModal &&
                contact.phone &&
                !shouldLockPremiumFeature
            ) {
                setContactsInvitationState((state) => ({
                    ...state,
                    [contact.id]: {
                        ...state[contact.id],
                        sms: true,
                    },
                }));
            } else if (
                showNoEmailModal &&
                contact.email &&
                !shouldLockPremiumFeature
            ) {
                setContactsInvitationState((state) => ({
                    ...state,
                    [contact.id]: {
                        ...state[contact.id],
                        email: true,
                    },
                }));
            }
        },
        [showNoEmailModal, showNoPhoneModal, shouldLockPremiumFeature],
    );
    const openBlockedPremiumFeaturesModal = useCallback(() => {
        setShowBlockedPremiumFeaturesModal(true);
    }, [setShowBlockedPremiumFeaturesModal]);

    const handleContactsReminderChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (shouldLockPremiumFeature) {
                openBlockedPremiumFeaturesModal();
            } else {
                form.handleChange(e);
            }
        },
        [shouldLockPremiumFeature, form, openBlockedPremiumFeaturesModal],
    );

    const closeInPastModal = useCallback(() => setShowInPastModal(false), []);
    const closeBlockedPremiumFeaturesModal = useCallback(
        () => setShowBlockedPremiumFeaturesModal(false),
        [],
    );

    const renderRSVPStatus = useCallback(
        (item: ContactModel) => {
            const contactRSVPStatus = contactsRSVPStatus[item.id];

            if (!contactRSVPStatus) return <></>;

            return (
                <ContactRSVPStatus
                    contact={item}
                    status={contactsRSVPStatus[item.id]}
                    hideEditButton
                />
            );
        },
        [contactsRSVPStatus],
    );

    const renderExtraContentComponent = useCallback(
        (item: ContactModel) => {
            const contactInvitation = contactsInvitationState[item.id];

            return (
                <>
                    <AppointmentInviteContactSection
                        state={contactsInvitationState}
                        onSmsClick={handleContactSmsClick}
                        onEmailClick={handleContactEmailClick}
                        onSmsDisabledClick={handleContactSmsDisabledClick}
                        onEmailDisabledClick={handleContactEmailDisabledClick}
                        disable={isInPast}
                        item={item}
                    />

                    {contactInvitation?.sms || contactInvitation?.email
                        ? renderRSVPStatus(item)
                        : null}
                </>
            );
        },
        [
            contactsInvitationState,
            handleContactEmailClick,
            handleContactEmailDisabledClick,
            handleContactSmsClick,
            handleContactSmsDisabledClick,
            isInPast,
            renderRSVPStatus,
        ],
    );

    const isUSRegion = regionStore.getRegion() === CountryCode.unitedStates;
    const dateInputFormat = isUSRegion
        ? 'dddd MMMM Do YYYY'
        : `dddd, MMMM DD [${t(
              'AddEntryForm:labels:date_input_placeholder',
          )}] YYYY`;

    const userReminderOptions = useMemo(
        () => createUserReminderOptions(form.values.userReminder),
        [form.values.userReminder],
    );

    const contactsReminderOptions = useMemo(
        () => createContactsReminderOptions(form.values.contactsReminderTime),
        [form.values.contactsReminderTime],
    );

    const customReminderOptions = CUSTOM_REMINDER_OPTIONS;

    const contactsReminderSectionVisible =
        form.values.contacts?.length > 0 && contactsReminderOptions.length > 0;

    const handleUserReminderChange = useCallback(
        (value: string) => {
            form.setFieldValue('userReminder', value);

            if (!form.values.contactsReminder) {
                if (contactsReminderAvailable) {
                    form.setFieldValue('contactsReminderTime', value);
                }
            }
        },
        [form, contactsReminderAvailable],
    );

    return (
        <Modal
            isOpen
            center
            disableRestoreFocus
            testID="AddEditScheduleModal"
            styles="py-0 relative  h-[85%]"
        >
            <Box
                component="form"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                <Box className="sticky top-0 z-10">
                    <AddEditModalHeader
                        backgroundColor={ENTITY_COLOR.schedule}
                        cancel={handleFormCancelButtonClick}
                        save={form.handleSubmit}
                        testIdPrefix="AddEditScheduleForm"
                        title={t(
                            mode === 'edit'
                                ? 'EditEvent:view_title'
                                : 'NewEvent:view_title',
                        )}
                        teamName={
                            userOrganisations && userOrganisations?.length > 1
                                ? userOrganisation?.name || ''
                                : undefined
                        }
                        avatarUrl={
                            userOrganisations && userOrganisations?.length > 1
                                ? getImageDisplayUrl(teamAvatar) ?? ''
                                : undefined
                        }
                        icon={<ScheduleIcon color={COLOR.white} />}
                        sx={{ mb: 0 }}
                    />
                    <Box>
                        <Box className="relative">
                            {!userModel?.isAppointmentOnboarded ? (
                                <Spotlight
                                    width={tabsLayout.width}
                                    height="100%"
                                    sx={{ px: 6 }}
                                />
                            ) : null}
                            <Tabs
                                tabs={tabItems}
                                initialTabValue={event?.category}
                                activeColor={ENTITY_COLOR.schedule}
                                onTabChange={handleTabChange}
                                sx={{ pl: 6, backgroundColor: COLOR.white }}
                                onTabsLayout={handleTabsLayout}
                            />
                        </Box>
                        {mode !== 'edit' && isServiceType ? (
                            <Box className="flex gap-3 bg-whiteSmoke pl-[61px] py-[6px]">
                                <FormControlLabel
                                    sx={{ mr: 0 }}
                                    checked={form.values.createEntries}
                                    control={
                                        <Checkbox
                                            id="createEntries"
                                            name="createEntries"
                                            data-test-id="AddEditScheduleForm-CreateEntries"
                                            onBlur={form.handleBlur}
                                            onChange={form.handleChange}
                                            checked={
                                                form.values.horses?.length
                                                    ? form.values.createEntries
                                                    : false
                                            }
                                            disabled={
                                                !form.values.horses?.length
                                            }
                                        />
                                    }
                                    label={t('Event:button:create_entries')}
                                />
                                {!form.values.horses.length ? (
                                    <Tooltip
                                        title={t(
                                            'Event:label:create_entries_disabled_description',
                                        )}
                                    >
                                        <InfoOutlinedIcon
                                            sx={{
                                                display: 'flex',
                                                alignSelf: 'center',
                                                color: COLOR.bahamaBlue,
                                            }}
                                        />
                                    </Tooltip>
                                ) : null}
                            </Box>
                        ) : null}
                    </Box>
                </Box>

                <Box
                    ref={scrollContainerRef}
                    className={`px-12 ${
                        userModel?.isAppointmentOnboarded ? 'pb-7' : ''
                    } flex flex-col gap-10`}
                    sx={{
                        overflow: 'auto',
                        py: 3,
                    }}
                >
                    <TextField
                        fullWidth
                        data-test-id="AddEditScheduleForm-Title"
                        id="title"
                        label={t('Event:label:title')}
                        error={!!title.error && !!title.touched}
                        helperText={title.touched ? title.error : ''}
                        onBlur={handleTitleBlur}
                        onChange={handleTitleChange}
                        value={title.value}
                        name="title"
                    />
                    <Box className="flex flex-col gap-5">
                        <Box className="flex gap-3">
                            {organisationUsers?.length ? (
                                <MembersSelection
                                    label={t('AddEventForm:labels:members')}
                                    members={organisationUsers ?? []}
                                    selectedMembersIds={form.values.membersIds}
                                    id="membersIds"
                                    error={
                                        !!form.errors.membersIds &&
                                        !!form.touched.membersIds
                                    }
                                    helperText={
                                        form.touched.membersIds
                                            ? form.errors.membersIds
                                            : ''
                                    }
                                    onBlur={form.handleBlur}
                                    onChange={form.handleChange}
                                    name="membersIds"
                                    entity="events"
                                    sx={{ flex: 2 }}
                                />
                            ) : null}
                        </Box>
                    </Box>
                    <Box className="flex gap-3">
                        <Box className="flex-1">
                            <DesktopDatePicker
                                label={t('AddEventForm:labels:date')}
                                value={form.values.date}
                                onChange={onDateChanged}
                                inputFormat={dateInputFormat}
                                renderInput={renderDateInput}
                                onClose={() => form.validateField('date')}
                                disableMaskedInput
                            />
                        </Box>
                        <Box className="flex flex-1 gap-3">
                            <TimePicker
                                label={t('AddEventForm:labels:start_time')}
                                value={form.values.startTime}
                                onChange={onStartTimeChanged}
                                inputFormat="hh:mm a"
                                renderInput={renderStartTimeInput}
                                maxTime={form.values.endTime}
                                disableOpenPicker
                            />
                            <TimePicker
                                label={t('AddEventForm:labels:end_time')}
                                value={form.values.endTime}
                                onChange={onEndTimeChanged}
                                inputFormat="hh:mm a"
                                renderInput={renderEndTimeInput}
                                minTime={form.values.startTime}
                                disableOpenPicker
                            />
                        </Box>
                    </Box>

                    <>
                        <TextField
                            select
                            id="timezone"
                            label={t('Event:label:timezone')}
                            onBlur={form.handleBlur}
                            onChange={form.handleChange}
                            name="timezone"
                            value={form.values.timezone}
                            data-test-id="AddEditScheduleForm-Timezone"
                            className="flex-1"
                            SelectProps={{
                                renderValue: renderSelectedTimezone,
                            }}
                        >
                            {selectableTimezones.map(
                                ({ timezone, offset, displayName }) => {
                                    const currentTime = Moment().tz(timezone);

                                    return (
                                        <MenuItem
                                            key={timezone}
                                            value={timezone}
                                            data-test-id={`AddEditScheduleForm-Timezone${timezone}`}
                                            className="flex-col gap-1 items-start"
                                        >
                                            <Box>{displayName}</Box>
                                            <Box>{`${currentTime
                                                .format('LT')
                                                .toLowerCase()} - ${offset}`}</Box>
                                        </MenuItem>
                                    );
                                },
                            )}
                        </TextField>
                        <Box className="flex flex-col gap-4">
                            <Typography
                                sx={{
                                    fontSize: 15,
                                    lineHeight: '24px',
                                    color: COLOR.ebonyClay,
                                    fontWeight: 700,
                                }}
                            >
                                {t('Event:label:remind_me')}
                            </Typography>
                            <Box className="flex flex-row gap-3">
                                <SelectWithCustomInput
                                    id="userReminder"
                                    label={t('Event:label:timing')}
                                    error={!willSendUserAlert}
                                    helperText={
                                        !willSendUserAlert
                                            ? t('Event:errors:user_reminder')
                                            : ''
                                    }
                                    onBlur={form.handleBlur}
                                    onChange={handleUserReminderChange}
                                    name="userReminder"
                                    selectedOption={
                                        form.values.userReminder
                                            ? getOptionFromReminderValue(
                                                  form.values.userReminder,
                                              )
                                            : userReminderOptions[0]
                                    }
                                    data-test-id="AddEditScheduleForm-Alert"
                                    sx={{ flex: 1 }}
                                    options={userReminderOptions}
                                    customOptions={customReminderOptions}
                                />
                                <Box className="flex flex-row flex-1 gap-2 px-3 py-2 rounded opacity-70 bg-athensGrayDarker">
                                    <Smartphone fontSize="small" />
                                    <Typography className="flex-1 text-xs">
                                        {t('Event:label:reminder_web')}{' '}
                                        <a
                                            href={APPLE_APP_STORE_URL}
                                            className="underline text-ebonyClay decoration-solid decoration-ebonyClay decoration-1"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            iOS
                                        </a>{' '}
                                        {t('Common:or')}{' '}
                                        <a
                                            href={GOOGLE_PLAY_URL}
                                            className="underline text-ebonyClay decoration-solid decoration-ebonyClay decoration-1"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Android
                                        </a>
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </>

                    <Box
                        id="selectContactsContainer"
                        className="flex relative flex-col gap-3"
                    >
                        <SelectContactsSection
                            onSelectContacts={handleSelectContacts}
                            onRemoveContact={handleRemoveContact}
                            selectedContacts={form.values.contacts}
                            CreateContactModal={AddEditContactModal}
                            CreateHorseModal={AddEditHorseModal}
                            buttonsPosition="top"
                            testIdPrefix="AddEditScheduleForm"
                            allowHorsesSelection
                            displayContactInformation
                            renderExtraContentComponent={
                                renderExtraContentComponent
                            }
                            renderSelectContactModalItemContent={
                                renderRSVPStatus
                            }
                        />
                        {contactsReminderSectionVisible ? (
                            <Box className="flex flex-row gap-2 min-h-[56px]">
                                <Box className="flex flex-col flex-1 gap-2">
                                    <FormControlLabel
                                        sx={{
                                            mr: 0,
                                            alignItems: 'center',
                                            fontSize: 16,
                                        }}
                                        checked={form.values.contactsReminder}
                                        control={
                                            <Checkbox
                                                id="contactsReminder"
                                                name="contactsReminder"
                                                data-test-id="AddEditScheduleForm-Notify"
                                                onBlur={form.handleBlur}
                                                onChange={
                                                    handleContactsReminderChange
                                                }
                                                checked={
                                                    form.values.contactsReminder
                                                }
                                                disabled={
                                                    !form.values.contacts
                                                        ?.length
                                                }
                                                sx={{
                                                    py: 0,
                                                    opacity:
                                                        shouldLockPremiumFeature
                                                            ? 0.3
                                                            : 1,
                                                }}
                                            />
                                        }
                                        label={
                                            <Box className="flex flex-row gap-2 items-center">
                                                {shouldLockPremiumFeature && (
                                                    <HttpsIcon
                                                        style={{
                                                            width: 16,
                                                            height: 16,
                                                            color: COLOR.deepCerulean,
                                                        }}
                                                    />
                                                )}
                                                <Typography
                                                    fontSize={14}
                                                    sx={{
                                                        color: COLOR.nevada,
                                                        opacity:
                                                            shouldLockPremiumFeature
                                                                ? 0.3
                                                                : 1,
                                                    }}
                                                >
                                                    {t(
                                                        'Event:button:remind_contacts',
                                                    )}
                                                </Typography>
                                            </Box>
                                        }
                                    />

                                    <Typography
                                        className="text-xs ml-8 max-w-[280px]"
                                        sx={{
                                            color: COLOR.nevada,
                                            opacity: shouldLockPremiumFeature
                                                ? 0.3
                                                : 1,
                                        }}
                                    >
                                        {t(
                                            'Event:label:remind_contacts_description',
                                        )}
                                    </Typography>
                                </Box>
                                <Box className="flex flex-1">
                                    {form.values.contactsReminder ? (
                                        <SelectWithCustomInput
                                            id="contactsReminderTime"
                                            label={t('Event:label:timing')}
                                            error={!willSendContactsAlert}
                                            helperText={
                                                !willSendContactsAlert
                                                    ? t(
                                                          'Event:errors:user_reminder',
                                                      )
                                                    : ''
                                            }
                                            onBlur={form.handleBlur}
                                            onChange={form.handleChange(
                                                'contactsReminderTime',
                                            )}
                                            name="contactsReminderTime"
                                            selectedOption={
                                                form.values.contactsReminderTime
                                                    ? getOptionFromReminderValue(
                                                          form.values
                                                              .contactsReminderTime,
                                                      )
                                                    : contactsReminderOptions[0]
                                            }
                                            data-test-id="AddEditScheduleForm-Alert"
                                            sx={{ flex: 1 }}
                                            options={contactsReminderOptions}
                                            customOptions={
                                                customReminderOptions
                                            }
                                        />
                                    ) : null}
                                </Box>
                            </Box>
                        ) : null}
                    </Box>
                    {isServiceType ? (
                        <SelectHorsesSection
                            contactsToFilterHorsesBy={form.values.contacts}
                            onRemoveHorse={handleRemoveHorse}
                            onSelectHorses={handleSelectHorses}
                            selectedHorses={form.values.horses}
                            CreateHorseModal={AddEditHorseModal}
                            CreateContactModal={AddEditContactModal}
                            variant="outlined"
                            testIdPrefix="AddEditScheduleForm"
                            allowContactsSelection
                        />
                    ) : null}
                    <GooglePlacesAutocomplete
                        label={t('AddContactForm:labels:address')}
                        onBlur={form.handleBlur}
                        onPlaceChange={handlePlaceChange}
                        testId="AddEditScheduleForm-Location"
                        value={
                            form.values.location
                                ? {
                                      description: form.values.location.name,
                                      place_id: form.values.location.placeId,
                                  }
                                : form.values.address
                                ? {
                                      description: form.values.address,
                                      place_id: '',
                                  }
                                : null
                        }
                    />
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        id="notes"
                        data-test-id="AddEditScheduleForm-Notes"
                        label={t('AddHorseForm:labels:notes')}
                        error={!!form.errors.notes && !!form.touched.notes}
                        helperText={form.touched.notes ? form.errors.notes : ''}
                        onBlur={form.handleBlur}
                        onChange={form.handleChange}
                        name="notes"
                        value={form.values.notes}
                    />
                </Box>
                {mode === 'edit' ? (
                    <>
                        <TooltipSyncButton
                            tooltip={t('AddEditModalHeader:button_tooltip', {
                                defaultValue:
                                    'Please wait for the synchronization to complete',
                            })}
                            onClick={showRemoveEventAlert}
                            showTooltip={isSyncInProgress}
                            disabled={isSyncInProgress}
                            testID="AddEditScheduleForm-RemoveButton"
                            sx={{ mt: 3, alignSelf: 'center' }}
                            stylesAt="container"
                            color="error"
                            startIcon={<DeleteOutlineRoundedIcon />}
                        >
                            {t('Event:button:remove')}
                        </TooltipSyncButton>
                        <RemoveEntityAlert
                            removeMessage={t(
                                'Actions:cancel_with_confirmation',
                            )}
                            cancelMessage={t('Actions:go_back')}
                            dialogText={t('Event:alert:remove_event')}
                            isOpen={isRemoveEventAlertVisible}
                            onClose={closeRemoveEventAlert}
                            onRemove={handleRemoveEvent}
                            testID="RemoveScheduleAlert"
                        />
                    </>
                ) : null}
            </Box>
            {showCancelAlert ? renderCancelAlert() : null}
            {!userModel?.isAppointmentOnboarded ? (
                <AppointmentOnboardingModal
                    onContinue={dismissAppointmentOnboarding}
                />
            ) : null}
            {showNoPhoneModal || showNoEmailModal ? (
                <NoAppointmentContactDetailsModal
                    type={showNoPhoneModal ? 'sms' : 'email'}
                    onEdit={handleEditContact}
                    onCancel={handleCloseNoDetailsModal}
                />
            ) : null}
            {showEditContactModal ? (
                <AddEditContactModal
                    contact={contactToEdit}
                    close={closeEditContactModal}
                    onSave={handleSaveContact}
                    isEditMode
                    CreateHorseModal={AddEditHorseModal}
                    enableRemoveContact={false}
                />
            ) : null}
            {showInPastModal ? (
                <AppointmentInvitationInPastModal onClose={closeInPastModal} />
            ) : null}
            {showBlockedPremiumFeaturesModal ? (
                <BlockedPremiumFeatureModal
                    close={closeBlockedPremiumFeaturesModal}
                />
            ) : null}
        </Modal>
    );
}

export default AddEditScheduleModal;
